/**
 * SmartGroupPanel — explains smart grouping decisions and lets users refine.
 *
 * Shows which fields were chosen, why, and allows toggling fields on/off
 * or providing free-text feedback to re-run the grouping differently.
 */

import { memo, useState, useCallback, type CSSProperties } from "react";
import type {
  SmartGroupExplanation,
  SmartGroupOverrides,
  DataGroup,
  FieldReasonKind,
} from "./groupDetection";
import { parseFeedback } from "./groupDetection";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const panelStyle: CSSProperties = {
  padding: "8px 12px",
  borderBottom: "1px solid var(--gray-a5)",
  fontSize: 12,
  color: "var(--gray-11)",
  background: "var(--gray-a2)",
};

const chipStyle = (active: boolean): CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "2px 8px",
  borderRadius: 12,
  border: `1px solid ${active ? "var(--accent-a6)" : "var(--gray-a5)"}`,
  background: active ? "var(--accent-a3)" : "var(--gray-a2)",
  color: active ? "var(--accent-11)" : "var(--gray-10)",
  fontSize: 11,
  cursor: "pointer",
  transition: "all 0.15s",
  whiteSpace: "nowrap",
});

const toggleDot = (active: boolean): CSSProperties => ({
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: active ? "var(--accent-9)" : "var(--gray-a6)",
  flexShrink: 0,
});

const reasonBadgeStyle: CSSProperties = {
  fontSize: 9,
  padding: "0 4px",
  borderRadius: 3,
  background: "var(--gray-a3)",
  color: "var(--gray-9)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const btnStyle: CSSProperties = {
  padding: "3px 10px",
  borderRadius: 4,
  border: "1px solid var(--gray-a6)",
  fontSize: 11,
  cursor: "pointer",
  background: "var(--gray-a3)",
  color: "var(--gray-12)",
};

const suggestionChipStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 8px",
  borderRadius: 10,
  border: "1px solid var(--gray-a4)",
  background: "var(--gray-a2)",
  color: "var(--gray-10)",
  fontSize: 10,
  cursor: "pointer",
  transition: "all 0.15s",
  whiteSpace: "nowrap",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const REASON_LABELS: Record<FieldReasonKind, string> = {
  categorical_name: "categorical",
  low_cardinality: "low cardinality",
  source_field: "data source",
  user_forced: "manual",
  grouping_keyword: "keyword match",
};

function reasonLabel(reason: FieldReasonKind): string {
  return REASON_LABELS[reason] ?? reason;
}

/** Human-readable explanation of why a field was chosen or rejected. */
function reasonExplanation(
  field: string,
  reason: FieldReasonKind,
  uniqueValues: number,
  ratio: number,
  sampleValues: string[],
  accepted: boolean,
): string {
  const pct = (ratio * 100).toFixed(0);
  const samples = sampleValues.length > 0 ? sampleValues.join(", ") : "n/a";
  const prefix = accepted ? "Used" : "Available";

  switch (reason) {
    case "categorical_name":
      return `${prefix}: "${field}" looks categorical (name pattern). ${uniqueValues} distinct values (${pct}% of rows). Values: ${samples}`;
    case "low_cardinality":
      return `${prefix}: "${field}" has few unique values relative to row count (${uniqueValues} values = ${pct}% of rows). Values: ${samples}`;
    case "source_field":
      return `${prefix}: "_source" identifies which dataset each row came from. ${uniqueValues} sources: ${samples}`;
    case "user_forced":
      return `${prefix}: You manually added "${field}" to the grouping. ${uniqueValues} values: ${samples}`;
    case "grouping_keyword":
      return `${prefix}: "${field}" contains a grouping keyword (group/type/config/case/variant/scenario/condition/mesh). ${uniqueValues} values: ${samples}`;
    default:
      return `${prefix}: "${field}" — ${uniqueValues} values (${pct}% of rows). Values: ${samples}`;
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SmartGroupPanelProps {
  explanation: SmartGroupExplanation;
  overrides: SmartGroupOverrides;
  onOverridesChange: (overrides: SmartGroupOverrides) => void;
  /** All column field names for feedback parsing. */
  allFields: string[];
  /** The actual groups produced — used to show per-group breakdown. */
  groups?: DataGroup[];
  /** Whether the panel is collapsed. */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SmartGroupPanel = memo(function SmartGroupPanel({
  explanation,
  overrides,
  onOverridesChange,
  allFields,
  groups,
  collapsed,
  onToggleCollapsed,
}: SmartGroupPanelProps) {
  const [feedback, setFeedback] = useState("");
  const [feedbackStatus, setFeedbackStatus] = useState<string | null>(null);
  const [showReasoning, setShowReasoning] = useState(false);
  const [showGroups, setShowGroups] = useState(false);

  const toggleField = useCallback(
    (field: string, currentlyUsed: boolean) => {
      const include = new Set(overrides.include ?? []);
      const exclude = new Set(overrides.exclude ?? []);

      if (currentlyUsed) {
        exclude.add(field);
        include.delete(field);
      } else {
        include.add(field);
        exclude.delete(field);
      }

      onOverridesChange({
        include: include.size > 0 ? [...include] : undefined,
        exclude: exclude.size > 0 ? [...exclude] : undefined,
      });
    },
    [overrides, onOverridesChange],
  );

  const handleFeedback = useCallback(() => {
    if (!feedback.trim()) return;
    const parsed = parseFeedback(feedback, allFields, explanation);

    const include = new Set([...(overrides.include ?? []), ...(parsed.include ?? [])]);
    const exclude = new Set([...(overrides.exclude ?? []), ...(parsed.exclude ?? [])]);
    for (const f of include) exclude.delete(f);

    onOverridesChange({
      include: include.size > 0 ? [...include] : undefined,
      exclude: exclude.size > 0 ? [...exclude] : undefined,
    });

    const actions: string[] = [];
    if (parsed.include?.length) actions.push(`added ${parsed.include.join(", ")}`);
    if (parsed.exclude?.length) actions.push(`removed ${parsed.exclude.join(", ")}`);
    if (parsed.hint === "reduce") actions.push("reducing group count");
    if (parsed.hint === "expand") actions.push("expanding groups");

    setFeedbackStatus(
      actions.length > 0
        ? `Applied: ${actions.join("; ")}`
        : "Couldn't parse specific field names. Try: \"ignore fieldname\" or \"group by fieldname\"",
    );
    setFeedback("");
    setTimeout(() => setFeedbackStatus(null), 6000);
  }, [feedback, allFields, explanation, overrides, onOverridesChange]);

  const applySuggestion = useCallback((text: string) => {
    setFeedback(text);
  }, []);

  const handleResetOverrides = useCallback(() => {
    onOverridesChange({});
  }, [onOverridesChange]);

  const hasOverrides = (overrides.include?.length ?? 0) > 0 || (overrides.exclude?.length ?? 0) > 0;
  const { fieldsUsed, fieldsRejected, groupCount, totalRows, gapSplitApplied, gapSplitField } = explanation;

  // Build a narrative summary
  const narrativeParts: string[] = [];
  if (fieldsUsed.length === 0) {
    narrativeParts.push("No grouping fields detected — all data shown as one group.");
  } else {
    const fieldNames = fieldsUsed.map((f) => f.headerName ?? f.field);
    narrativeParts.push(
      `Grouping ${totalRows} rows into ${groupCount} group${groupCount !== 1 ? "s" : ""} using ${fieldNames.join(" + ")}.`,
    );
  }
  if (gapSplitApplied && gapSplitField) {
    narrativeParts.push(`Some groups were further split where large gaps appeared in "${gapSplitField}".`);
  }
  if (hasOverrides) {
    const parts: string[] = [];
    if (overrides.include?.length) parts.push(`manually added: ${overrides.include.join(", ")}`);
    if (overrides.exclude?.length) parts.push(`manually removed: ${overrides.exclude.join(", ")}`);
    narrativeParts.push(`Your overrides: ${parts.join("; ")}.`);
  }

  // Suggestion chips for common actions
  const suggestions: { label: string; text: string }[] = [];
  if (fieldsUsed.length > 1) {
    suggestions.push({ label: "Too many groups", text: "too many groups" });
  }
  if (fieldsRejected.length > 0) {
    suggestions.push({ label: "Too few groups", text: "too few groups" });
    // Suggest adding the most promising rejected field
    const best = fieldsRejected[0];
    if (best) {
      suggestions.push({
        label: `Add ${best.headerName ?? best.field}`,
        text: `group by ${best.field}`,
      });
    }
  }
  if (fieldsUsed.length > 0) {
    // Suggest removing the field contributing most groups
    const worst = [...fieldsUsed].sort((a, b) => b.uniqueValues - a.uniqueValues)[0];
    if (worst && fieldsUsed.length > 1) {
      suggestions.push({
        label: `Drop ${worst.headerName ?? worst.field}`,
        text: `ignore ${worst.field}`,
      });
    }
  }

  return (
    <div style={panelStyle}>
      {/* Header row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
        }}
        onClick={onToggleCollapsed}
      >
        <span style={{ fontSize: 10, color: "var(--gray-8)", userSelect: "none" }}>
          {collapsed ? "\u25B6" : "\u25BC"}
        </span>
        <span style={{ fontWeight: 500, color: "var(--gray-12)" }}>
          Smart Groups
        </span>
        <span style={{ color: "var(--gray-9)", fontSize: 11 }}>
          {narrativeParts[0]}
        </span>
        {gapSplitApplied && (
          <span
            style={{
              ...reasonBadgeStyle,
              background: "var(--amber-a3)",
              color: "var(--amber-11)",
            }}
            title={`Large gaps in "${gapSplitField}" caused some groups to be split into sub-groups`}
          >
            gap-split
          </span>
        )}
        {hasOverrides && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleResetOverrides();
            }}
            style={{
              ...btnStyle,
              fontSize: 10,
              padding: "1px 6px",
              marginLeft: "auto",
              color: "var(--amber-11)",
              borderColor: "var(--amber-a6)",
            }}
            title="Reset all manual overrides and re-detect from scratch"
          >
            Reset overrides
          </button>
        )}
      </div>

      {!collapsed && (
        <div style={{ marginTop: 8 }}>
          {/* Narrative explanation */}
          {narrativeParts.length > 1 && (
            <div style={{ fontSize: 11, color: "var(--gray-10)", marginBottom: 8, lineHeight: 1.4 }}>
              {narrativeParts.slice(1).join(" ")}
            </div>
          )}

          {/* Active fields */}
          {fieldsUsed.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 10, color: "var(--gray-9)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Grouping by
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {fieldsUsed.map((f) => (
                  <span
                    key={f.field}
                    style={chipStyle(true)}
                    onClick={() => toggleField(f.field, true)}
                    title={reasonExplanation(f.field, f.reason, f.uniqueValues, f.ratio, f.sampleValues, true) + "\nClick to remove from grouping."}
                  >
                    <span style={toggleDot(true)} />
                    <span>{f.headerName ?? f.field}</span>
                    <span style={reasonBadgeStyle}>{reasonLabel(f.reason)}</span>
                    <span style={{ color: "var(--gray-9)" }}>
                      {f.uniqueValues} values
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Rejected / available fields */}
          {fieldsRejected.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 10, color: "var(--gray-9)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Also available (click to add)
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {fieldsRejected.map((f) => (
                  <span
                    key={f.field}
                    style={chipStyle(false)}
                    onClick={() => toggleField(f.field, false)}
                    title={reasonExplanation(f.field, f.reason, f.uniqueValues, f.ratio, f.sampleValues, false) + "\nClick to add to grouping."}
                  >
                    <span style={toggleDot(false)} />
                    <span>{f.headerName ?? f.field}</span>
                    <span style={reasonBadgeStyle}>{reasonLabel(f.reason)}</span>
                    <span style={{ color: "var(--gray-8)" }}>
                      {f.uniqueValues} values
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* No fields found */}
          {fieldsUsed.length === 0 && fieldsRejected.length === 0 && (
            <div style={{ color: "var(--gray-9)", fontStyle: "italic", marginBottom: 6 }}>
              No categorical or low-cardinality fields detected. All data is in a single group.
              Try adding a field manually using the feedback input below.
            </div>
          )}

          {/* Expandable reasoning detail */}
          {fieldsUsed.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <button
                onClick={() => setShowReasoning((v) => !v)}
                style={{
                  ...btnStyle,
                  fontSize: 10,
                  padding: "1px 6px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span style={{ fontSize: 9 }}>{showReasoning ? "\u25BC" : "\u25B6"}</span>
                Why these fields?
              </button>
              {showReasoning && (
                <div style={{
                  marginTop: 6,
                  padding: "6px 10px",
                  background: "var(--gray-a2)",
                  borderRadius: 6,
                  border: "1px solid var(--gray-a4)",
                  fontSize: 11,
                  lineHeight: 1.5,
                  color: "var(--gray-11)",
                }}>
                  {fieldsUsed.map((f) => (
                    <div key={f.field} style={{ marginBottom: 4 }}>
                      <span style={{ color: "var(--accent-11)", fontWeight: 500 }}>
                        {f.headerName ?? f.field}
                      </span>
                      {": "}
                      {reasonExplanation(f.field, f.reason, f.uniqueValues, f.ratio, f.sampleValues, true).replace(/^Used: /, "")}
                    </div>
                  ))}
                  {fieldsRejected.length > 0 && (
                    <>
                      <div style={{ marginTop: 6, marginBottom: 4, color: "var(--gray-9)", fontSize: 10, textTransform: "uppercase" }}>
                        Considered but not used:
                      </div>
                      {fieldsRejected.map((f) => (
                        <div key={f.field} style={{ marginBottom: 4, color: "var(--gray-9)" }}>
                          <span style={{ fontWeight: 500 }}>
                            {f.headerName ?? f.field}
                          </span>
                          {": "}
                          {reasonExplanation(f.field, f.reason, f.uniqueValues, f.ratio, f.sampleValues, false).replace(/^Available: /, "")}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Expandable per-group breakdown */}
          {groups && groups.length > 0 && groups.length <= 50 && (
            <div style={{ marginBottom: 6 }}>
              <button
                onClick={() => setShowGroups((v) => !v)}
                style={{
                  ...btnStyle,
                  fontSize: 10,
                  padding: "1px 6px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span style={{ fontSize: 9 }}>{showGroups ? "\u25BC" : "\u25B6"}</span>
                Show {groups.length} groups
              </button>
              {showGroups && (
                <div style={{
                  marginTop: 6,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                  maxHeight: 160,
                  overflowY: "auto",
                }}>
                  {groups.map((g) => (
                    <span
                      key={g.key}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "2px 8px",
                        borderRadius: 10,
                        border: "1px solid var(--gray-a4)",
                        background: "var(--gray-a2)",
                        fontSize: 11,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: g.color,
                        flexShrink: 0,
                      }} />
                      <span style={{ color: "var(--gray-12)" }}>{g.label}</span>
                      <span style={{ color: "var(--gray-8)", fontSize: 10 }}>
                        {g.rows.length} rows
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Feedback area */}
          <div style={{ marginTop: 4 }}>
            {/* Suggestion chips */}
            {suggestions.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                <span style={{ fontSize: 10, color: "var(--gray-8)", alignSelf: "center" }}>
                  Try:
                </span>
                {suggestions.map((s) => (
                  <span
                    key={s.text}
                    style={suggestionChipStyle}
                    onClick={() => applySuggestion(s.text)}
                    title={`Click to fill: "${s.text}"`}
                  >
                    {s.label}
                  </span>
                ))}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="text"
                placeholder={"Tell me what's wrong, e.g. \"ignore mach\", \"group by reynolds\", \"too many groups\""}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleFeedback();
                }}
                style={{
                  flex: 1,
                  padding: "4px 8px",
                  border: "1px solid var(--gray-a5)",
                  borderRadius: 4,
                  background: "var(--gray-a2)",
                  color: "var(--gray-12)",
                  fontSize: 11,
                  outline: "none",
                  minWidth: 200,
                }}
              />
              <button
                onClick={handleFeedback}
                disabled={!feedback.trim()}
                style={{
                  ...btnStyle,
                  opacity: feedback.trim() ? 1 : 0.4,
                  background: feedback.trim() ? "var(--accent-a4)" : "var(--gray-a3)",
                }}
              >
                Retry
              </button>
            </div>
            {feedbackStatus && (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  padding: "4px 8px",
                  borderRadius: 4,
                  background: feedbackStatus.startsWith("Couldn't")
                    ? "var(--amber-a3)"
                    : "var(--green-a3)",
                  color: feedbackStatus.startsWith("Couldn't")
                    ? "var(--amber-11)"
                    : "var(--green-11)",
                }}
              >
                {feedbackStatus}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});
